/**
 * ReportDialog — small modal to send a post report.
 * Category is optional; reason is required (server min length 3).
 */
import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { reportPost } from "@/lib/publishApi";
import { formatApiError } from "@/lib/api";

const CATEGORIES = [
  { value: "spam",          label: "Spam" },
  { value: "harassment",    label: "Harassment or abuse" },
  { value: "misinformation",label: "Misinformation" },
  { value: "inappropriate", label: "Inappropriate content" },
  { value: "copyright",     label: "Copyright" },
  { value: "other",         label: "Other" },
];

export function ReportDialog({ post, open, onOpenChange, onReported }) {
  const [category, setCategory] = useState("spam");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (reason.trim().length < 3) {
      toast.error("Please describe the issue (min 3 characters)");
      return;
    }
    setBusy(true);
    try {
      const res = await reportPost(post.id, reason.trim(), category);
      toast.success(res.post_status === "hidden"
        ? "Reported. The post was auto-hidden pending moderator review."
        : res.post_status === "deleted"
          ? "Reported. Threshold reached — post removed."
          : "Report submitted");
      onReported?.(res);
      onOpenChange(false);
      setReason(""); setCategory("spam");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="report-dialog">
        <DialogHeader>
          <DialogTitle>Report this post</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="report-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(c => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">What&apos;s wrong?</Label>
            <Textarea value={reason} onChange={e => setReason(e.target.value)}
                      placeholder="Tell moderators why this post is problematic…" rows={3}
                      data-testid="report-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-rose-600 hover:bg-rose-700"
                  data-testid="report-submit-btn">
            {busy ? "Submitting…" : "Submit report"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
